import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../components/DropSelectComponent';
import { Box, Divider, Typography } from '@mui/material';
import { useGetAllLprGroupByLicenseAsync } from '../../../services/LprGroupService/hooks/useMutation';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ILprGroupSimpleProps } from '../../../services/LprGroupService/types';
import ButtonComponent from '../../../components/ButtonComponent';
import ModalComponent from '../../../components/ModalComponent';
import EditIcon from '@mui/icons-material/Edit';
import LprGroupEditionModal from './LprGroupEditionModal';
import {
  useGetLprGroupsConfigAsync,
  useUpdateLprGroupsConfigAsync,
} from '../../../services/Devices/hooks/useMutation';
import { AppContext } from '../../../context/AppContextProvider';

interface DeviceLprGroupConfigProps {
  id: number;
  setIsPendingChanges: Dispatch<SetStateAction<boolean>>;
  setFunctionToConfirmPendingChanges: Dispatch<
    SetStateAction<() => Promise<void>>
  >;
}

const DeviceLprGroupConfig = ({
  id,
  setIsPendingChanges,
  setFunctionToConfirmPendingChanges,
}: DeviceLprGroupConfigProps) => {
  const [groups, setGroups] = useState<ILprGroupSimpleProps[]>([]);
  const { showSnackbar } = useContext(AppContext);
  const [viewPlateModal, setViewPlateModal] = useState<{
    isVisible: boolean;
    group: ILprGroupSimpleProps | undefined;
  }>({
    isVisible: false,
    group: undefined,
  });
  const [oldVal, setOldVal] = useState<number[]>([]);
  const [isVisibleLprGroupEdition, setIsVisibleLprGroupEdition] =
    useState(false);
  const [selectedGroupsIds, setSelectedGroupsIds] = useState<number[]>([]);

  const { mutateAsync: mutateGetAllLprGroupByLicenseAsync } =
    useGetAllLprGroupByLicenseAsync();

  const {
    mutateAsync: mutateUpdateLprGroupsConfigAsync,
    isLoading: isLoadingUpdateLprGroupsConfigAsync,
  } = useUpdateLprGroupsConfigAsync();

  const {
    mutateAsync: mutateGetLprGroupsConfigAsync,
    isLoading: isLoadingGetLprGroupsConfigAsync,
  } = useGetLprGroupsConfigAsync();

  const groupOptions: () => IDropSelectOptions[] = () => {
    return groups.map((g) => {
      return { option: g.name, value: g.id };
    });
  };

  const columns: IColumnProps[] = [
    { label: 'Nome', id: 'name' },
    {
      label: 'Placas',
      id: 'id',
      width: '20%',
      align: 'center',
      render(id: string) {
        return (
          <ButtonComponent
            size={'small'}
            onClick={() => handleViewGroupDetails(Number(id))}
          >
            Ver Placas
          </ButtonComponent>
        );
      },
    },
  ];

  const handleViewGroupDetails = (gpId: number) => {
    const gp = groups.find((g) => g.id === gpId);

    if (!gp) return;

    setViewPlateModal({
      group: gp,
      isVisible: true,
    });
  };

  const groupRenderTable = () => {
    return groups.filter((g) => selectedGroupsIds.includes(g.id));
  };

  const onChangeGroups = (val: number[]) => {
    setSelectedGroupsIds(val);
  };

  const onCloseViewPlateModal = () => {
    setViewPlateModal({
      group: undefined,
      isVisible: false,
    });
  };

  const handleSaveLprGroupConfig = async () => {
    await mutateUpdateLprGroupsConfigAsync({
      id,
      data: { lprGroupIds: selectedGroupsIds },
    }).then(() => {
      showSnackbar(
        'Configuração de grupo de placas atualizada com sucesso.',
        'success',
      );

      setOldVal(selectedGroupsIds);
      setIsPendingChanges(false);
    });
  };

  const getAllGroups = () => {
    mutateGetAllLprGroupByLicenseAsync().then(setGroups);
  };

  const getGroupConfig = () => {
    if (id) {
      mutateGetLprGroupsConfigAsync(id).then((v) => {
        setSelectedGroupsIds(v.lprGroupIds);
        setOldVal(v.lprGroupIds);
      });
    }
  };

  useEffect(() => {
    getAllGroups();
  }, []);

  useEffect(() => {
    getGroupConfig();
  }, [id]);

  useEffect(() => {
    if (selectedGroupsIds != oldVal) {
      setIsPendingChanges(true);
      setFunctionToConfirmPendingChanges(() => handleSaveLprGroupConfig);
    } else setIsPendingChanges(false);
  }, [selectedGroupsIds]);
  return (
    <>
      <DropSelectComponent
        label={'Seleção de grupos'}
        options={groupOptions()}
        isMultiple
        valueSelected={selectedGroupsIds}
        onChange={onChangeGroups}
        isRequired={false}
      />
      <Box marginTop={3}>
        <TableComponent
          rows={groupRenderTable()}
          columns={columns}
          emptyMessage={'Nenhuma grupo designado a esse dispositivo.'}
        />
      </Box>

      <Box marginTop={5}>
        <ButtonComponent
          fullWidth
          loading={isLoadingUpdateLprGroupsConfigAsync}
          onClick={() => handleSaveLprGroupConfig()}
        >
          Salvar
        </ButtonComponent>
      </Box>

      <ModalComponent
        onClose={onCloseViewPlateModal}
        isOpen={viewPlateModal.isVisible}
        title={
          'Visualização das placas do grupo: ' + viewPlateModal.group?.name
        }
      >
        <Typography marginBottom={4}>
          {viewPlateModal.group?.description}
        </Typography>
        {viewPlateModal.group?.plates.map((p, index) => (
          <Box key={index} marginBottom={2}>
            <Typography fontWeight={'bolder'}>{p}</Typography>
            <Divider sx={{ my: 2, backgroundColor: 'rgb(47, 57, 87)' }} />
          </Box>
        ))}
        <Box display="flex" justifyContent="flex-end">
          <Box display={'flex'} gap={2}>
            <ButtonComponent
              startIcon={<EditIcon />}
              onClick={() => setIsVisibleLprGroupEdition(true)}
            >
              Editar grupo
            </ButtonComponent>
            <ButtonComponent
              color={'error'}
              onClick={() => onCloseViewPlateModal()}
            >
              Fechar visualização
            </ButtonComponent>
          </Box>
        </Box>

        {viewPlateModal.group && (
          <LprGroupEditionModal
            isOpen={isVisibleLprGroupEdition}
            onClose={() => {
              // setViewPlateModal((oldVal) => ({ ...oldVal, group: updatedVal }));
              getAllGroups();
              getGroupConfig();
              setIsVisibleLprGroupEdition(false);
            }}
            selectedGroupToEdit={viewPlateModal.group}
          />
        )}
      </ModalComponent>
    </>
  );
};

export default DeviceLprGroupConfig;
